import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import { useState } from 'react';

import { useCanaryVisibilityScoreTrendByCompanyType } from '@/api/aiVisibility';
import { useCurrentInstance } from '@/api/organization';
import Flare, { Axis, Legend, Line, Tooltip } from '@/components/Flare';
import { Panel } from '@/components/panels';
import { MonthDateFormat, MonthDateYearFormat } from '@/constants/formats';
import { numberFormat } from '@/helper/numberFormatter';

const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;
const WINDOW_IN_MS = 90 * 24 * 60 * 60 * 1000;

const AIVisibilityOverviewTrendChart = () => {
  const { instance } = useCurrentInstance();
  const { trends, isLoading, error } = useCanaryVisibilityScoreTrendByCompanyType();
  const [windowEnd] = useState(() => Date.now());
  const windowStart = windowEnd - WINDOW_IN_MS;
  const firstWeek = dayjs(windowStart).utc().day(0).startOf('day').valueOf();
  const customerLabel = instance?.name ? t`${instance.name} (you)` : t`Your company (you)`;
  const competitorLabel = t`Competitor average`;
  const series = trends?.map(({ companyType, data }) => {
    const points = data
      .map(({ sundayOfWeek, visibilityScore }) => ({
        week: Date.parse(sundayOfWeek),
        score: visibilityScore,
        ...(companyType === 'customer' ? { marker: { enabled: true, radius: 4 } } : {}),
      }))
      .filter(({ week }) => week >= firstWeek && week <= windowEnd);

    return {
      id: companyType,
      name: companyType === 'customer' ? customerLabel : competitorLabel,
      dashStyle: companyType === 'competitor' ? 'ShortDash' : undefined,
      data: points.flatMap((point, index) => {
        const previousWeek = points[index - 1]?.week;
        return previousWeek != null && point.week - previousWeek > WEEK_IN_MS
          ? [{ week: previousWeek + WEEK_IN_MS, score: null }, point]
          : [point];
      }),
    };
  });
  const hasCustomerData = series?.some(
    ({ id, data }) => id === 'customer' && data.some(({ score }) => score != null),
  );
  const hasCompetitorData = series?.some(
    ({ id, data }) => id === 'competitor' && data.some(({ score }) => score != null),
  );
  const hasData = hasCustomerData || hasCompetitorData;
  const colors = trends?.map(({ companyType }) =>
    companyType === 'customer' ? 'var(--color-green-400)' : 'var(--color-gray-600)',
  );

  return (
    <Panel
      title={<Trans>Visibility trend</Trans>}
      subtitle={
        <Trans>Your visibility index vs. the competitor average over the last 90 days.</Trans>
      }
      className="min-h-64 min-w-0"
    >
      <div className="h-52 min-w-0">
        <Flare
          data={series == null ? undefined : hasData ? series : []}
          colors={colors}
          isLoading={isLoading}
          error={error}
          emptyMessage={<Trans>No visibility trend data available.</Trans>}
          options={{ plotOptions: { series: { connectNulls: false } } }}
        >
          <Legend />
          <Line x="week" y="score" />
          <Axis position="left" min={0} title={t`Visibility Score`} />
          <Axis
            type="datetime"
            position="bottom"
            min={firstWeek}
            max={windowEnd}
            labelFormat={(item) => dayjs(item.value).utc().format(MonthDateFormat)}
          />
          <Tooltip
            shared
            titleFormat={(point) => t`Week of ${dayjs(point.x).utc().format(MonthDateYearFormat)}`}
            rowValueFormat={(point) => numberFormat(point.y, { precision: 1 })}
          />
        </Flare>
      </div>
      {!isLoading && !error && hasData && !hasCustomerData && (
        <p className="text-xs text-gray-600">
          <Trans>No customer visibility data for this period.</Trans>
        </p>
      )}
      {!isLoading && !error && hasData && !hasCompetitorData && (
        <p className="text-xs text-gray-600">
          <Trans>No competitor average data for this period.</Trans>
        </p>
      )}
    </Panel>
  );
};

export default AIVisibilityOverviewTrendChart;
